import React, { useState } from 'react';
import { ValueRenderer } from './ValueRenderer';

import { useTranslation } from 'react-i18next';

interface JsonRendererProps {
  data: unknown;
  label?: string;
  depth?: number;
  isNested?: boolean;
}

export const JsonRenderer: React.FC<JsonRendererProps> = ({
  data,
  label,
  depth = 0,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [isExpanded, setIsExpanded] = useState(depth < 2);

  // Check if data is expandable
  const isExpandable = typeof data === 'object' && data !== null;

  // Render primitive value
  if (!isExpandable) {
    return (
      <div className="flex items-center rounded-md px-4 py-2.5">
        <div className="flex flex-1 items-center gap-3">
          <span className="min-w-[180px] font-medium text-gray-700 dark:text-gray-300">
            {label}
          </span>
          <ValueRenderer
            value={data as string | number | boolean | null | undefined}
            label={label}
          />
        </div>
      </div>
    );
  }

  const isArray = Array.isArray(data);
  const items = isArray ? data : Object.entries(data);
  const hasItems = isArray ? data.length > 0 : Object.keys(data).length > 0;
  const isEmpty = !hasItems;

  return (
    <div className="group">
      <div
        className={`flex items-center rounded-md px-4 py-2.5 transition-all ${hasItems ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-gray-800' : ''} ${isExpanded ? 'bg-gray-50/70 dark:bg-gray-800/70' : ''}`}
        onClick={() => hasItems && setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-1 items-center gap-3">
          <div className="flex items-center gap-2">
            {hasItems && (
              <span
                className={`text-gray-400 transition-transform duration-200 dark:text-gray-500 ${isExpanded ? 'rotate-90' : ''} group-hover:text-gray-600 dark:group-hover:text-gray-400`}
              >
                ›
              </span>
            )}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {label}
            </span>
          </div>

          <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
            {!isEmpty && (
              <span className="rounded-full bg-gray-100/80 px-2 py-0.5 text-sm dark:bg-gray-800/80">
                {isArray
                  ? t('items', { length: data.length })
                  : t('fields', { length: Object.keys(data).length })}
              </span>
            )}
            {hasItems && (
              <span className="min-w-[52px] text-xs text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-400">
                {isExpanded ? t('collapse') : t('expand')}
              </span>
            )}
          </div>
        </div>
      </div>
      {isExpanded && hasItems && (
        <div className="my-1 ml-6 border-l-2 border-gray-100 pl-6 dark:border-gray-800">
          {items.map((item, index) => {
            const [key, value] = isArray ? [index, item] : item;
            return (
              <JsonRenderer
                key={key}
                data={value}
                label={isArray ? `Item ${index + 1}` : String(key)}
                depth={depth + 1}
                isNested
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
